import { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material'
import { RequestService } from '../../services/RequestService'
import { useSnackbar } from '../../hooks'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import { useTranslation } from 'react-i18next'
import { useConfigContextInfo } from '../../contexts/ConfigContext'
import { PLANS } from '../../utils/planNames'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 32px 48px;
`

const TopBoxes = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 32px;
`

const BoxItem = styled.div`
  flex: 1;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  font-weight: bold;

  p {
    margin-top: 8px;
    font-size: 22px;
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 24px 0 0 0;
  gap: 8px;
`

const InlineField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const DataExport = () => {
  const [loading, setLoading] = useState(false)
  const loadingData = loading
  const { successSnackbar, errorSnackbar } = useSnackbar()
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()
  const startDate = new Date(currentYear, currentMonth, 1)
  const [dateRange, setDateRange] = useState([startDate, new Date()])
  const { t } = useTranslation('common')
  const context = useConfigContextInfo()

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      date_range: dateRange,
    }))
  }, [dateRange])

  const [formData, setFormData] = useState({
    date_range: dateRange,
    prediction_results: false,
    intake_history: false,
    caffeine_intake_history: false,
    pvt_history: false,
    system_event_history: false,
  })

  const handleInputChange = event => {
    setFormData(prevData => {
      const { name, value, type, checked } = event.target
      const newValue = type === 'checkbox' ? checked : value

      return { ...prevData, [name]: newValue }
    })
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setLoading(true)
    try {
      const response = await RequestService.exportData({
        formData,
      })
      if (response.status == 'success') {
        const blob = response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'relatorios.zip')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        setLoading(false)
        successSnackbar({ text: t('data_export.done') })
      } else {
        setLoading(false)
        errorSnackbar({ text: t('data_export.failed') })
      }
    } catch (e) {
      setLoading(false)
      errorSnackbar({ text: t('data_export.failed') })
    }
  }

  return (
    <Container>
      <h2>{t('data_export.title')}</h2>
      <FormContainer
        as='form'
        onSubmit={handleSubmit}
      >
        <CustomProvider locale={ptBR}>
          <DateRangePicker
            defaultCalendarValue={dateRange}
            value={dateRange}
            onChange={event => setDateRange(event)}
            showOneCalendar
            shouldDisableDate={afterToday()}
            character=' - '
            placement='bottom'
            placeholder={t('data_export.datepicker.placeholder')}
            ranges={[]}
            cleanable={false}
          />
        </CustomProvider>
        {(context?.plan === PLANS.OPTIMIZED ||
          context?.plan === PLANS.PREDICTION) && (
          <InlineField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.prediction_results}
                  onChange={handleInputChange}
                  name='prediction_results'
                />
              }
              label={t('data_export.prediction_results')}
            />
          </InlineField>
        )}
        <InlineField>
          <FormControlLabel
            control={
              <Switch
                checked={formData.intake_history}
                onChange={handleInputChange}
                name='intake_history'
              />
            }
            label={t('data_export.intake_history')}
          />
        </InlineField>
        <InlineField>
          <FormControlLabel
            control={
              <Switch
                checked={formData.caffeine_intake_history}
                onChange={handleInputChange}
                name='caffeine_intake_history'
              />
            }
            label={t('data_export.caffeine_intake_history')}
          />
        </InlineField>
        <InlineField>
          <FormControlLabel
            control={
              <Switch
                checked={formData.pvt_history}
                onChange={handleInputChange}
                name='pvt_history'
              />
            }
            label={t('data_export.pvt_history')}
          />
        </InlineField>
        <InlineField>
          <FormControlLabel
            control={
              <Switch
                checked={formData.system_event_history}
                onChange={handleInputChange}
                name='system_event_history'
              />
            }
            label={t('data_export.system_event_history')}
          />
        </InlineField>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <Button
            variant='contained'
            color='primary'
            type='submit'
          >
            {t('data_export.download')}
          </Button>
        )}
      </FormContainer>
    </Container>
  )
}

export default DataExport
